import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import HttpFetchStatus from '@common/enums/api/http-fetch-status';
import {
  defaultRobotRestSet,
  defaultTorizonSet,
  getInitialRobotsState,
  RobotRestSet,
} from './robots-slice-definitions';
import {
  createRobot,
  getDailyCleanedAreaById,
  getMissionById,
  getMissions,
  getOrganizationRobots,
  getRobotById,
  getWireguardConfigById,
  getTorizonStatus,
  postTorizonUpdate,
  postTorizonUpdateCancel,
  getDeploymentsByRobotId,
  patchDeploymentByRobotId,
  patchDeploymentDatesByRobotId,
  reassignMissionsByRobotId,
  patchRobotAuthenticationSettingsById,
  patchRobotById,
  deleteRobotById,
  getContactsByRobotId,
  getLocalRobotDataStateById,
  getTags,
  postTag,
  patchTagById,
  deleteTagById,
} from './robots-actions';
import { updateDataList } from '../../utils/generic-data-list-handlers';
import RestSet from '@common/interfaces/api/rest-set';
import WebSocketConnectionStatus from '../../models/enums/websocket-connection-status';
import WebSocketMessage from '../../models/misc/api/websocket-message';
import WebSocketMessageType from '../../models/enums/websocket-message-type';
import { isSyncDetailsInstance } from '../../models/robots/sync-details';
import { isRobotStatus } from '../../models/robots/robot-status';
import { isMissionList } from '../../models/missions/mission-list';
import { isActionsList } from '../../models/robots/actions-list';
import { isLocalRobotDataState } from 'models/robots/local-robot-data-state';
import { isRobotSituation } from 'models/robots/robot-situation';
import CleaningImage from '@common/interfaces/cleaning-map/cleaning-image';
import { isTorizonReadiness } from '../../models/robots/torizon-readiness';
import { isTorizonUpdateState } from '../../models/robots/torizon-update-state';
import TorizonUpdateStatus from '../../models/enums/torizon-update-status';

const initialRobotsState = getInitialRobotsState();

const robotsSlice = createSlice({
  name: 'robots',
  initialState: initialRobotsState,
  reducers: {
    resetRobotsState(state) {
      return {
        ...initialRobotsState,
        robots: {
          ...initialRobotsState.robots,
          rest: { ...initialRobotsState.robots.rest, getOrgId: state.robots.rest.getOrgId },
        },
      };
    },
    resetRobotsRestState(state, action: PayloadAction<Partial<RobotRestSet>>) {
      state.robots.rest = { ...state.robots.rest, ...action.payload };
    },
    setSelectedRobotId(state, action: PayloadAction<string | null>) {
      state.robots.selectedId = action.payload;

      state.robots.rest.getRobotInformationStatus = HttpFetchStatus.Idle;
      state.robots.rest.getRobotInformationError = null;

      state.robots.rest.getDeploymentsStatus = HttpFetchStatus.Idle;
      state.robots.rest.getDeploymentsError = null;

      state.robots.rest.getContactsStatus = HttpFetchStatus.Idle;
      state.robots.rest.getContactsError = null;

      state.robots.rest.getLocalRobotDataStatus = HttpFetchStatus.Idle;
      state.robots.rest.getLocalRobotDataError = null;

      state.robots.currentRobotData = {};
    },
    resetMissionsRestState(state, action: PayloadAction<Partial<RestSet>>) {
      state.missions.rest = { ...state.missions.rest, ...action.payload };
    },
    setSelectedMissionId(state, action: PayloadAction<string | null>) {
      if (action.payload !== state.missions.selectedId) {
        state.missions.rest.getMissionInformationStatus = HttpFetchStatus.Idle;
        state.missions.rest.getMissionInformationError = null;
      }
      state.missions.selectedId = action.payload;
      if (action.payload == null) {
        state.missions.currentMission = null;
      }
    },
    resetCurrentRobot(state) {
      state.robots.currentRobot = null;
      state.torizon = defaultTorizonSet;
    },
    setTorizonReadinessPending(state, action: PayloadAction<boolean>) {
      state.torizon.readinessPending = action.payload;
      if (action.payload) {
        state.torizon.readiness = null;
      }
    },
    resetTorizonState(state) {
      state.torizon = defaultTorizonSet;
    },
    handleWebsocketMessage(state, action: PayloadAction<WebSocketMessage>) {
      switch (action.payload.type) {
        case WebSocketMessageType.SYNC_STATUS:
          if (
            state.robots.currentRobot?.authentication != null &&
            isSyncDetailsInstance(action.payload.data)
          ) {
            state.robots.currentRobot.authentication.syncDetails = action.payload.data;
          }
          break;
        case WebSocketMessageType.ROBOT_STATUS: {
          if (!isRobotSituation(action.payload.data)) return;

          const status = action.payload.data.status;
          if (!isRobotStatus(status)) return;

          const selectedRobot = state.robots.pagination?.data.find((r) => r.id === status.robotId);
          if (selectedRobot?.status != null) {
            selectedRobot.status = status;
          }

          if (state.robots.currentRobot?.id === status.robotId) {
            state.robots.currentRobot.status = status;
            state.robots.currentRobotData = {
              ...state.robots.currentRobotData,
              missionStatus: action.payload.data.mission,
              schedulingStatus: action.payload.data.scheduling,
              statusTimestamp: new Date().toISOString(),
            };
          }
          break;
        }
        case WebSocketMessageType.CLEANING_IMAGE: {
          const cleaningImage = action.payload.data as CleaningImage;
          if (
            cleaningImage == null ||
            cleaningImage.robotId == null ||
            state.robots.currentRobot?.id !== cleaningImage.robotId
          ) {
            return;
          }

          state.robots.currentRobotData = {
            ...state.robots.currentRobotData,
            position: cleaningImage.robotPosition ?? state.robots.currentRobotData.position,
            coverageMap: cleaningImage.coverageMap ?? state.robots.currentRobotData.coverageMap,
            statusTimestamp: new Date().toISOString(),
          };
          break;
        }
        case WebSocketMessageType.MISSION: {
          const mission = action.payload.data;
          if (!isMissionList(mission)) return;

          if (
            state.missions.pagination != null &&
            (mission.deployment.robotId === state.robots.selectedId ||
              state.robots.selectedId == null)
          ) {
            state.missions.pagination.data = updateDataList(
              mission,
              state.missions.pagination.data,
            );
          }
          break;
        }
        case WebSocketMessageType.ACTIONS: {
          const actionsList = action.payload.data;
          if (!isActionsList(actionsList)) return;
          state.robots.currentRobotData = { ...state.robots.currentRobotData, actionsList };
          break;
        }
        case WebSocketMessageType.UNSYNCED_CHANGES: {
          const localDataState = action.payload.data;
          if (!isLocalRobotDataState(localDataState)) return;
          state.robots.currentRobotData = { ...state.robots.currentRobotData, localDataState };
          break;
        }
        case WebSocketMessageType.TORIZON_UPDATE_STATE: {
          const updateState = action.payload.data;
          if (!isTorizonUpdateState(updateState)) return;

          const listedRobot = state.robots.pagination?.data.find(
            (r) => r.id === updateState.robotId,
          );
          if (listedRobot?.status != null) {
            listedRobot.status.torizonUpdateStatus =
              updateState.torizonUpdateStatus as TorizonUpdateStatus;
          }

          if (
            state.robots.currentRobot?.id === updateState.robotId &&
            state.robots.currentRobot.status != null
          ) {
            const justSucceeded =
              updateState.torizonUpdateStatus === TorizonUpdateStatus.SUCCESS &&
              state.robots.currentRobot.status.torizonUpdateStatus !== TorizonUpdateStatus.SUCCESS;
            state.robots.currentRobot.status.torizonUpdateStatus =
              updateState.torizonUpdateStatus as TorizonUpdateStatus;
            // The old answer ("update available") is stale now; the card refetches it.
            if (justSucceeded) state.torizon.status = null;
          }
          break;
        }
        case WebSocketMessageType.TORIZON_UPDATE_READY: {
          const readiness = action.payload.data;
          if (!isTorizonReadiness(readiness)) return;
          state.torizon.readiness = readiness;
          state.torizon.readinessPending = false;
          break;
        }
        default:
          break;
      }
    },
    setRobotHubConnectionStatus(state, action: PayloadAction<WebSocketConnectionStatus>) {
      state.robots.robotHubState = action.payload;
    },
    patchTagsRestSet(state, action: PayloadAction<Partial<RestSet>>) {
      state.tags.rest = { ...state.tags.rest, ...action.payload };
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getOrganizationRobots.pending, (state, action) => {
      state.robots.rest.getStatus = HttpFetchStatus.Loading;
      state.robots.rest.getOrgId = action.meta.arg.org ?? null;
      state.robots.pagination = null;
    });
    builder.addCase(getOrganizationRobots.fulfilled, (state, action) => {
      state.robots.rest.getStatus = HttpFetchStatus.Success;
      if (action.meta.arg.org === state.robots.rest.getOrgId) {
        // No coalescing here: nothing renders torizonUpdateStatus off the robot list,
        // only off currentRobot.
        state.robots.pagination = action.payload ?? null;
      }
    });
    builder.addCase(getOrganizationRobots.rejected, (state, action) => {
      state.robots.rest.getStatus = HttpFetchStatus.Failed;
      state.robots.rest.getError = action.payload ?? null;
    });
    builder.addCase(getRobotById.pending, (state, action) => {
      state.robots.rest.getRobotInformationStatus = HttpFetchStatus.Loading;
      state.robots.currentRobot = null;
    });
    builder.addCase(getRobotById.fulfilled, (state, action) => {
      state.robots.rest.getRobotInformationStatus = HttpFetchStatus.Success;
      state.robots.currentRobot = action.payload;

      state.robots.rest.getDailyCleanedAreaStatus = HttpFetchStatus.Idle;
      state.robots.rest.getDailyCleanedAreaError = null;

      state.robots.rest.getWireguardConfigStatus = HttpFetchStatus.Idle;
      state.robots.rest.getWireguardConfigError = null;
    });
    builder.addCase(getRobotById.rejected, (state, action) => {
      state.robots.rest.getRobotInformationStatus = HttpFetchStatus.Failed;
      state.robots.rest.getRobotInformationError = action.payload ?? null;
    });
    builder.addCase(deleteRobotById.pending, (state, action) => {
      state.robots.rest.deleteStatus = HttpFetchStatus.Loading;
    });
    builder.addCase(deleteRobotById.fulfilled, (state, action) => {
      state.robots.currentRobot = null;
      state.robots.currentRobotData = {};

      const robotIndex = state.robots.pagination?.data.findIndex((r) => r.id === action.payload);
      if (robotIndex != null && robotIndex !== -1 && state.robots.pagination != null) {
        state.robots.pagination.data.splice(robotIndex, 1);
        state.robots.pagination.count--;
      }
      state.robots.rest = { ...defaultRobotRestSet };
      state.robots.rest.deleteStatus = HttpFetchStatus.Success;
    });
    builder.addCase(deleteRobotById.rejected, (state, action) => {
      state.robots.rest.deleteStatus = HttpFetchStatus.Failed;
      state.robots.rest.deleteError = action.payload ?? null;
    });
    builder.addCase(getDailyCleanedAreaById.pending, (state, action) => {
      state.robots.rest.getDailyCleanedAreaStatus = HttpFetchStatus.Loading;
    });
    builder.addCase(getDailyCleanedAreaById.fulfilled, (state, action) => {
      state.robots.rest.getDailyCleanedAreaStatus = HttpFetchStatus.Success;
      if (action.meta.arg.id !== state.robots.currentRobot?.id) return;
      state.robots.currentRobotData = {
        ...state.robots.currentRobotData,
        dailyCleanedArea: action.payload,
      };
    });
    builder.addCase(getDailyCleanedAreaById.rejected, (state, action) => {
      state.robots.rest.getDailyCleanedAreaStatus = HttpFetchStatus.Failed;
      state.robots.rest.getDailyCleanedAreaError = action.payload ?? null;
    });
    builder.addCase(getWireguardConfigById.pending, (state, action) => {
      state.robots.rest.getWireguardConfigStatus = HttpFetchStatus.Loading;
      state.robots.rest.getWireguardConfigError = null;
    });
    builder.addCase(getWireguardConfigById.fulfilled, (state, action) => {
      state.robots.rest.getWireguardConfigStatus = HttpFetchStatus.Success;
      if (action.meta.arg !== state.robots.currentRobot?.id) return;

      state.robots.currentRobotData = {
        ...state.robots.currentRobotData,
        wireguardConfig: action.payload,
      };
    });
    builder.addCase(getWireguardConfigById.rejected, (state, action) => {
      state.robots.rest.getWireguardConfigStatus = HttpFetchStatus.Failed;
      state.robots.rest.getWireguardConfigError = action.payload ?? null;
    });
    builder.addCase(getTorizonStatus.pending, (state) => {
      state.torizon.rest.getStatus = HttpFetchStatus.Loading;
      state.torizon.rest.getError = null;
    });
    builder.addCase(getTorizonStatus.fulfilled, (state, action) => {
      if (action.meta.arg !== state.robots.currentRobot?.id) return;
      state.torizon.rest.getStatus = HttpFetchStatus.Success;
      state.torizon.status = action.payload ?? null;
    });
    builder.addCase(getTorizonStatus.rejected, (state, action) => {
      if (action.meta.arg !== state.robots.currentRobot?.id) return;
      state.torizon.rest.getStatus = HttpFetchStatus.Failed;
      state.torizon.rest.getError = action.payload ?? null;
    });
    builder.addCase(postTorizonUpdate.pending, (state) => {
      state.torizon.rest.updateStatus = HttpFetchStatus.Loading;
      state.torizon.rest.updateError = null;
    });
    builder.addCase(postTorizonUpdate.fulfilled, (state, action) => {
      state.torizon.rest.updateStatus = HttpFetchStatus.Success;
      const robotId = action.meta.arg;

      const listedRobot = state.robots.pagination?.data.find((r) => r.id === robotId);
      if (listedRobot?.status != null) {
        listedRobot.status.torizonUpdateStatus = TorizonUpdateStatus.PENDING;
      }

      if (state.robots.currentRobot?.id === robotId && state.robots.currentRobot.status != null) {
        state.robots.currentRobot.status.torizonUpdateStatus = TorizonUpdateStatus.PENDING;
      }
    });
    builder.addCase(postTorizonUpdate.rejected, (state, action) => {
      state.torizon.rest.updateStatus = HttpFetchStatus.Failed;
      state.torizon.rest.updateError = action.payload ?? null;
    });
    builder.addCase(postTorizonUpdateCancel.pending, (state) => {
      state.torizon.rest.cancelStatus = HttpFetchStatus.Loading;
      state.torizon.rest.cancelError = null;
    });
    builder.addCase(postTorizonUpdateCancel.fulfilled, (state) => {
      state.torizon.rest.cancelStatus = HttpFetchStatus.Success;
    });
    builder.addCase(postTorizonUpdateCancel.rejected, (state, action) => {
      state.torizon.rest.cancelStatus = HttpFetchStatus.Failed;
      state.torizon.rest.cancelError = action.payload ?? null;
    });
    builder.addCase(getDeploymentsByRobotId.pending, (state) => {
      state.robots.rest.getDeploymentsStatus = HttpFetchStatus.Loading;
      state.robots.rest.getDeploymentsError = null;
    });
    builder.addCase(getDeploymentsByRobotId.fulfilled, (state, action) => {
      state.robots.rest.getDeploymentsStatus = HttpFetchStatus.Success;
      if (action.meta.arg !== state.robots.currentRobot?.id) return;
      state.robots.currentRobotData = {
        ...state.robots.currentRobotData,
        deployments: action.payload,
      };
    });
    builder.addCase(getDeploymentsByRobotId.rejected, (state, action) => {
      state.robots.rest.getDeploymentsStatus = HttpFetchStatus.Failed;
      state.robots.rest.getDeploymentsError = action.payload ?? null;
    });
    builder.addCase(patchDeploymentByRobotId.pending, (state, action) => {
      state.robots.rest.patchDeploymentsStatus = HttpFetchStatus.Loading;
      state.robots.rest.patchDeploymentsError = null;
    });
    builder.addCase(patchDeploymentByRobotId.fulfilled, (state, action) => {
      state.robots.rest.patchDeploymentsStatus = HttpFetchStatus.Success;
      const existingDeploymentIndex = state.robots.currentRobotData?.deployments?.findIndex(
        (d) => d.id === action.payload.id,
      );
      if (existingDeploymentIndex != null && existingDeploymentIndex !== -1) {
        state.robots.currentRobotData!.deployments![existingDeploymentIndex] = action.payload;
      }
      if (state.missions.pagination != null) {
        state.missions.pagination.data
          .filter((m) => m.deployment.id === action.payload.id)
          .forEach((m) => {
            m.deployment.organizationId = action.payload.organizationId;
            m.deployment.organizationName = action.payload.organizationName;
          });
      }
    });
    builder.addCase(patchDeploymentByRobotId.rejected, (state, action) => {
      state.robots.rest.patchDeploymentsStatus = HttpFetchStatus.Failed;
      state.robots.rest.patchDeploymentsError = action.payload ?? null;
    });
    builder.addCase(patchDeploymentDatesByRobotId.pending, (state, action) => {
      state.robots.rest.patchDeploymentDatesStatus = HttpFetchStatus.Loading;
      state.robots.rest.patchDeploymentDatesError = null;
    });
    builder.addCase(patchDeploymentDatesByRobotId.fulfilled, (state, action) => {
      state.robots.rest.patchDeploymentDatesStatus = HttpFetchStatus.Success;
      if (
        action.meta.arg.robotId === state.robots.currentRobot?.id &&
        state.robots.currentRobotData?.deployments != null
      ) {
        state.robots.currentRobotData.deployments = action.payload;
      }
    });
    builder.addCase(patchDeploymentDatesByRobotId.rejected, (state, action) => {
      state.robots.rest.patchDeploymentDatesStatus = HttpFetchStatus.Failed;
      state.robots.rest.patchDeploymentDatesError = action.payload ?? null;
    });
    builder.addCase(reassignMissionsByRobotId.pending, (state) => {
      state.robots.rest.reassignMissionsStatus = HttpFetchStatus.Loading;
      state.robots.rest.reassignMissionsError = null;
    });
    builder.addCase(reassignMissionsByRobotId.fulfilled, (state, action) => {
      state.robots.rest.reassignMissionsStatus = HttpFetchStatus.Success;
      if (state.robots.currentRobotData?.deployments != null) {
        state.robots.currentRobotData.deployments = action.payload;
      }
    });
    builder.addCase(reassignMissionsByRobotId.rejected, (state, action) => {
      state.robots.rest.reassignMissionsStatus = HttpFetchStatus.Failed;
      state.robots.rest.reassignMissionsError = action.payload ?? null;
    });
    builder.addCase(getContactsByRobotId.pending, (state, action) => {
      state.robots.rest.getContactsStatus = HttpFetchStatus.Loading;
      state.robots.rest.getContactsError = null;
    });
    builder.addCase(getContactsByRobotId.fulfilled, (state, action) => {
      if (action.meta.arg !== state.robots.selectedId) return;
      state.robots.currentRobotData = {
        ...state.robots.currentRobotData,
        contacts: action.payload,
      };
      state.robots.rest.getContactsStatus = HttpFetchStatus.Success;
    });
    builder.addCase(getContactsByRobotId.rejected, (state, action) => {
      state.robots.rest.getContactsStatus = HttpFetchStatus.Failed;
      state.robots.rest.getContactsError = action.payload ?? null;
    });
    builder.addCase(getLocalRobotDataStateById.pending, (state, action) => {
      state.robots.rest.getLocalRobotDataStatus = HttpFetchStatus.Loading;
      state.robots.rest.getLocalRobotDataError = null;
    });
    builder.addCase(getLocalRobotDataStateById.fulfilled, (state, action) => {
      if (action.meta.arg !== state.robots.selectedId) return;
      state.robots.currentRobotData = {
        ...state.robots.currentRobotData,
        localDataState: action.payload,
      };
      state.robots.rest.getLocalRobotDataStatus = HttpFetchStatus.Success;
    });
    builder.addCase(getLocalRobotDataStateById.rejected, (state, action) => {
      state.robots.rest.getLocalRobotDataStatus = HttpFetchStatus.Failed;
      state.robots.rest.getLocalRobotDataError = action.payload ?? null;
    });
    builder.addCase(createRobot.pending, (state, action) => {
      state.robots.rest.postStatus = HttpFetchStatus.Loading;
    });
    builder.addCase(createRobot.fulfilled, (state, action) => {
      state.robots.rest.postStatus = HttpFetchStatus.Success;
      if (state.robots.pagination != null) {
        // No coalescing here: nothing renders torizonUpdateStatus off the robot list,
        // only off currentRobot.
        state.robots.pagination.data.push(action.payload);
        state.robots.pagination.count++;
      }
    });
    builder.addCase(createRobot.rejected, (state, action) => {
      state.robots.rest.postStatus = HttpFetchStatus.Failed;
      state.robots.rest.postError = action.payload ?? null;
    });
    builder.addCase(patchRobotById.pending, (state, action) => {
      state.robots.rest.patchStatus = HttpFetchStatus.Loading;
      state.robots.rest.patchId = action.meta.arg.id;
    });
    builder.addCase(patchRobotById.fulfilled, (state, action) => {
      state.robots.rest.patchStatus = HttpFetchStatus.Success;
      const patchedRobot = action.payload;

      if (state.robots.pagination?.data != null) {
        const existing_robot_index = state.robots.pagination.data.findIndex(
          (robot) => robot.id === patchedRobot.id,
        );
        if (existing_robot_index !== -1) {
          const existing_robot = state.robots.pagination.data[existing_robot_index];
          if (existing_robot.organizationId !== patchedRobot.organizationId) {
            // robot has been moved to different organization
            state.robots.pagination.data.splice(existing_robot_index, 1);
          } else {
            state.robots.pagination.data = updateDataList(
              patchedRobot,
              state.robots.pagination.data,
            );
          }
        }
      }
      if (state.robots.currentRobot?.id === patchedRobot.id) {
        state.robots.currentRobot = patchedRobot;
      }
    });
    builder.addCase(patchRobotById.rejected, (state, action) => {
      state.robots.rest.patchStatus = HttpFetchStatus.Failed;
      state.robots.rest.patchError = action.payload ?? null;
    });
    builder.addCase(patchRobotAuthenticationSettingsById.pending, (state, action) => {
      state.robots.rest.patchAuthenticationSettingsStatus = HttpFetchStatus.Loading;
    });
    builder.addCase(patchRobotAuthenticationSettingsById.fulfilled, (state, action) => {
      state.robots.rest.patchAuthenticationSettingsStatus = HttpFetchStatus.Success;
      if (state.robots.selectedId === action.meta.arg.id && state.robots.currentRobot != null) {
        state.robots.currentRobot.authentication = action.payload;
      }
    });
    builder.addCase(patchRobotAuthenticationSettingsById.rejected, (state, action) => {
      state.robots.rest.patchAuthenticationSettingsStatus = HttpFetchStatus.Failed;
      state.robots.rest.patchAuthenticationSettingsError = action.payload ?? null;
    });
    builder.addCase(getMissions.pending, (state, action) => {
      state.missions.rest.getStatus = HttpFetchStatus.Loading;
      state.missions.pagination = null;
      state.missions.currentMission = null;
      state.missions.selectedId = null;
    });
    builder.addCase(getMissions.fulfilled, (state, action) => {
      state.missions.rest.getStatus = HttpFetchStatus.Success;
      state.missions.pagination = action.payload ?? null;
    });
    builder.addCase(getMissions.rejected, (state, action) => {
      state.missions.rest.getStatus = HttpFetchStatus.Failed;
      state.missions.rest.getError = action.payload ?? null;
    });
    builder.addCase(getMissionById.pending, (state, action) => {
      state.missions.rest.getMissionInformationStatus = HttpFetchStatus.Loading;
      state.missions.currentMission = null;
    });
    builder.addCase(getMissionById.fulfilled, (state, action) => {
      state.missions.rest.getMissionInformationStatus = HttpFetchStatus.Success;
      state.missions.currentMission = action.payload ?? null;
    });
    builder.addCase(getMissionById.rejected, (state, action) => {
      state.missions.rest.getMissionInformationStatus = HttpFetchStatus.Failed;
      state.missions.rest.getMissionInformationError = action.payload ?? null;
    });

    builder.addCase(getTags.pending, (state, action) => {
      state.tags.rest.getStatus = HttpFetchStatus.Loading;
    });
    builder.addCase(getTags.fulfilled, (state, action) => {
      state.tags.rest.getStatus = HttpFetchStatus.Success;
      state.tags.data = action.payload ?? null;
    });
    builder.addCase(getTags.rejected, (state, action) => {
      state.tags.rest.getStatus = HttpFetchStatus.Failed;
      state.tags.rest.getError = action.payload ?? null;
    });
    builder.addCase(postTag.pending, (state, action) => {
      state.tags.rest.postStatus = HttpFetchStatus.Loading;
    });
    builder.addCase(postTag.fulfilled, (state, action) => {
      state.tags.rest.postStatus = HttpFetchStatus.Success;
      if (state.robots.currentRobot?.id === action.payload.robot) {
        state.robots.currentRobot.tags.push(action.payload);
      }
      if (state.tags.data != null && !state.tags.data.includes(action.payload.tag)) {
        state.tags.data.push(action.payload.tag);
      }
    });
    builder.addCase(postTag.rejected, (state, action) => {
      state.tags.rest.postStatus = HttpFetchStatus.Failed;
      state.tags.rest.postError = action.payload ?? null;
    });
    builder.addCase(patchTagById.pending, (state, action) => {
      state.tags.rest.patchStatus = HttpFetchStatus.Loading;
    });
    builder.addCase(patchTagById.fulfilled, (state, action) => {
      state.tags.rest.patchStatus = HttpFetchStatus.Success;
      const tagIndex = state.robots.currentRobot?.tags.findIndex(
        (t) => t.id === action.meta.arg.id,
      );
      if (state.robots.currentRobot != null && tagIndex != null && tagIndex !== -1) {
        state.robots.currentRobot.tags[tagIndex] = action.payload;
      }
      if (state.tags.data != null && !state.tags.data.includes(action.payload.tag)) {
        state.tags.data.push(action.payload.tag);
      }
    });
    builder.addCase(patchTagById.rejected, (state, action) => {
      state.tags.rest.patchStatus = HttpFetchStatus.Failed;
      state.tags.rest.patchError = action.payload ?? null;
    });
    builder.addCase(deleteTagById.pending, (state, action) => {
      state.tags.rest.deleteStatus = HttpFetchStatus.Loading;
    });
    builder.addCase(deleteTagById.fulfilled, (state, action) => {
      state.tags.rest.deleteStatus = HttpFetchStatus.Success;
      const tagIndex = state.robots.currentRobot?.tags.findIndex((t) => t.id === action.payload);
      if (state.robots.currentRobot != null && tagIndex != null && tagIndex !== -1) {
        state.robots.currentRobot.tags.splice(tagIndex, 1);
      }
    });
    builder.addCase(deleteTagById.rejected, (state, action) => {
      state.tags.rest.deleteStatus = HttpFetchStatus.Failed;
      state.tags.rest.deleteError = action.payload ?? null;
    });
  },
});

export const {
  resetRobotsState,
  resetRobotsRestState,
  resetCurrentRobot,
  setSelectedRobotId,
  resetMissionsRestState,
  setSelectedMissionId,
  handleWebsocketMessage,
  setRobotHubConnectionStatus,
  patchTagsRestSet,
  setTorizonReadinessPending,
  resetTorizonState,
} = robotsSlice.actions;

export default robotsSlice.reducer;
